import {
  Chip,
  Stack,
  Tooltip,
  Typography,
  Avatar,
  AvatarGroup,
  alpha,
  SvgIcon,
  Card,
  CardContent,
  Button,
  IconButton,
  Box,
  Popper,
  Paper,
  ClickAwayListener,
  debounce,
  Skeleton,
  TextField,
  Autocomplete,
  InputAdornment,
  TooltipProps,
  tooltipClasses,
  Link,
} from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridColumnGroupingModel,
  GridColumnHeaders,
  GridPaginationModel,
  GridRenderCellParams,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridLogicOperator,
  GridToolbar,
  useGridApiContext,
  useGridRootProps,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useSearchTermsGridQuery } from 'src/context/api/non-branded/api';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings/selectors';
import { RootState } from 'src/context/store';
import { fDate } from 'src/utils/format-time';
import { fPercent, fNumber, formatNumber } from 'src/utils/format-number';
import { formatCurrency } from 'src/utils/format-currency';
import Iconify from 'src/components/iconify';
import ColumnStackVertical from './column-stack-vertical';
import Chart from 'src/components/chart';
import CursorClickIcon from './icons/cursor-click-icon';
import TextIcon from './icons/text-icon';
import ShoppingCartIcon from './icons/shopping-cart';
import CompetitorsTooltip from './tooltips/competitors';
import PaidSearchShareTooltip from './tooltips/paid-search-share';
import ClicksSplitTooltip from './tooltips/clicks-split';
import PaidShoppingShareTooltip from './tooltips/paid-shopping-share';
import { CategoryButton } from './category-button';
import ExportIcon from './icons/export';
import { FilterPanelCustom } from 'src/components/data-grid/filter-panel-custom';
import { styled } from '@mui/material/styles';
import { useLocales } from 'src/locales';
import PopperCell from './popper-cell';
import KeywordDetailModal from '../keyword-detail-modal';
import { useNonBrandedContext } from '../context/use-non-branded';
import { RHFAutocomplete } from 'src/components/hook-form';
import DiffIcon from '../diff-icon';
import { useGetCountriesQuery } from 'src/context/api/countries/api';
import { useGetBQCountriesQuery } from 'src/context/api';

const GridPanelRoot = styled(Popper, {
  name: 'MuiDataGrid',
  slot: 'Panel',
  overridesResolver: (props, styles) => styles.panel,
})(({ theme }) => ({
  zIndex: (theme as any).zIndex.modal,
}));
const GridPaperRoot = styled(Paper, {
  name: 'MuiDataGrid',
  slot: 'Paper',
  overridesResolver: (props, styles) => styles.paper,
})(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  minWidth: 300,
  maxHeight: 450,
  display: 'flex',
  maxWidth: `calc(100vw - ${theme.spacing(0.5)})`,
  overflow: 'auto',
}));

const colorRange = [
  {
    min: 0,
    max: 30,
    color: '#F5CE47',
  },
  {
    min: 30,
    max: 70,
    color: '#BC9510',
  },
  {
    min: 70,
    max: 100,
    color: '#18A155',
  },
];

const getSeoPositionChipColor = (position: number): string => {
  if (position <= 2) return 'rgba(193, 144, 193, 0.5)';
  if (position <= 4) return 'rgba(171, 124, 171, 0.5)';
  if (position <= 6) return 'rgba(156, 124, 156, 0.5)';
  if (position <= 8) return 'rgba(147, 133, 147, 0.5)';
  return 'rgba(107, 107, 107, 0.5)';
};

const mockData = (id: number) => ({
  id,
  search_term: `search_term_${id}`,
  total_clicks: 100,
  percent_ad_clicks_from_search: 10,
  percent_ad_clicks_from_shopping: 10,
  percent_organic_clicks: 10,
  visibility_rate: 10,
  avg_seo_position: 10,
  paid_search_visibility_rate: 10,
  first_search_visibility_rate: 10,
  top_search_visibility_rate: 10,
  compet_search_visibility_rate: 10,
  roas: 10,
  top_compet__process: [],
  top_competitors: [],
  top_competitors_process: [],
  opportunity: 'no opportunity',
  top5_compet_total: 10,
});

const useSearchTermsGridData = (
  paginationModel: GridPaginationModel & GridFilterModel & { countries: string[] },
  sortModel: GridSortModel
) => {
  const workspace = useCurrentWorkspace();
  const { selectedOpportunity } = useNonBrandedContext();
  const start = useSelector((state: RootState) => state.filters.start);
  const end = useSelector((state: RootState) => state.filters.end);
  const compare = useSelector((state: RootState) => state.filters.compare);
  const compareStart = useSelector((state: RootState) => state.filters.compareStart);
  const compareEnd = useSelector((state: RootState) => state.filters.compareEnd);
  const devices = useSelector((state: RootState) => state.filters.devices);
  const platforms = useSelector((state: RootState) => state.filters.platforms);
  const searchTerms = useSelector((state: RootState) => state.filters.keywords);
  const {
    data: searchTermsGrid,
    isLoading,
    isError,
    isFetching,
  } = useSearchTermsGridQuery({
    workspace_id: workspace?.id as number,
    start_date: fDate(start, 'yyyy-MM-dd'),
    end_date: fDate(end, 'yyyy-MM-dd'),
    start_compare: fDate(compareStart, 'yyyy-MM-dd'),
    end_compare: fDate(compareEnd, 'yyyy-MM-dd'),
    devices: devices,
    countries: paginationModel.countries || [],
    search_terms: searchTerms,
    platforms: platforms,
    limit: paginationModel.pageSize,
    page: paginationModel.page,
    filterModel: paginationModel.items,
    opportunity: selectedOpportunity,
    sort_field: sortModel[0]?.field,
    sort_order: sortModel[0]?.sort as string,
  });

  return {
    searchTermsGrid,
    isLoading,
    isError,
    isFetching,
  };
};

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}));

const useColumns = (paidRoasField?: string, topCompetField?: string) => {
  const {
    currentLang: { value: locale },
    t,
  } = useLocales();
  const workspace = useCurrentWorkspace();
  const countries = useGetBQCountriesQuery();
  const { selectedRegion } = useNonBrandedContext() as { selectedRegion: string };
  const valueOrEmpty = (value: any, format: (value: any) => string = String) =>
    !!value ? format(value) : '-';
  const formatToCurrency = (value: any, digits: number = 1) =>
    formatCurrency(value, workspace?.currency, locale, true, digits);
  const formatPercent = (value: any) => {
    if (value <= 1) return value + '%';
    const fixed = value.toFixed(0);
    return fixed.endsWith('.0') ? fixed.slice(0, -2) + '%' : fixed + '%';
  };

  const country = countries.data?.find((country) => Object.values(country).map((c) => c.toLowerCase()).includes(selectedRegion?.toLowerCase()));

  const popperRef = useRef<{ rowId: null | string }>({ rowId: null });
  const compare = useSelector((state: RootState) => state.filters.compare);

  return [
    {
      field: 'search_term',
      headerName: t('dashboard.nonBrand.searchTerms'),
      type: 'string',
      renderHeader(params: any) {
        return (
          <LightTooltip title={t('dashboard.nonBrand.tooltips.clicks')}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                {params.colDef.headerName}
              </Typography>
              <Box flexGrow={1} />

              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                style={{ minWidth: '16px', height: '16px' }}
                fill="none"
              >
                <g clipPath="url(#clip0_1128_7170)">
                  <path
                    d="M5.99992 2.33301V1.33301M3.37369 3.37345L2.66659 2.66634M3.37369 8.66634L2.66659 9.37345M8.66659 3.37345L9.37369 2.66634M2.33325 5.99967H1.33325M10.5762 10.7927L8.91508 13.8777C8.7253 14.2301 8.63042 14.4063 8.51625 14.4509C8.41718 14.4896 8.3056 14.4787 8.2159 14.4215C8.11255 14.3557 8.05363 14.1644 7.93579 13.7818L5.63004 6.29651C5.53378 5.98401 5.48565 5.82776 5.52438 5.72223C5.55811 5.63031 5.63055 5.55787 5.72247 5.52414C5.82801 5.48541 5.98425 5.53354 6.29675 5.6298L13.7821 7.93557C14.1646 8.05341 14.3559 8.11233 14.4217 8.21569C14.4789 8.30538 14.4898 8.41696 14.4511 8.51604C14.4066 8.6302 14.2303 8.72509 13.8779 8.91486L10.793 10.576C10.7406 10.6042 10.7144 10.6183 10.6915 10.6364C10.6711 10.6525 10.6527 10.6709 10.6367 10.6912C10.6185 10.7142 10.6044 10.7404 10.5762 10.7927Z"
                    stroke="#5E6D7B"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_1128_7170">
                    <rect width="16" height="16" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </Stack>
          </LightTooltip>
        );
      },
      flex: 1,
      minWidth: 175,
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography
            variant="body2"
            textOverflow="ellipsis"
            overflow="hidden"
            noWrap
            sx={{ fontWeight: 600 }}
          >
           <Link href={`https://www.google.com/search?q=${params.row.search_term}&gl=${country?.country_code}&hl=${country?.country_language}`} target="_blank" rel="noopener noreferrer" underline="none" color="inherit" sx={{ textDecoration: 'none' }}>
            {params.row.search_term}
            </Link>
          </Typography>
          <Stack flexGrow={1} minWidth={2} />
          <LightTooltip title={t('dashboard.nonBrand.columns.clicks')}>
            <Typography
              variant="body2"
              sx={{
                color: '#919EAB',
                fontVariantNumeric: 'lining-nums tabular-nums',
                fontFamily: 'Public Sans',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '22px',
              }}
              color="text.secondary"
            >
              {fNumber(params.row.total_clicks)}
            </Typography>
          </LightTooltip>
        </Box>
      ),
    },
    {
      field: 'clicks_split',
      headerName: t('dashboard.nonBrand.columns.clicksSplit'),
      cellClassName: 'column--data-column',
      disableColumnMenu: true,
      flex: 1,
      filterable: false,
      minWidth: 220,
      renderHeader: (params: GridRenderCellParams) => {
        return (
          <LightTooltip title={t('dashboard.nonBrand.tooltips.clicksSplit')}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                {params.colDef.headerName}
              </Typography>
            </Stack>
          </LightTooltip>
        );
      },
      renderCell: (params: GridRenderCellParams) => {
        return (
          <PopperCell
            popperRef={popperRef}
            rowId={params.row.id}
            popper={
              <ClicksSplitTooltip
                percentAdClicksFromSearch={params.row.percent_ad_clicks_from_search}
                percentAdClicksFromShopping={params.row.percent_ad_clicks_from_shopping}
                percentOrganicClicks={params.row.percent_organic_clicks}
              />
            }
          >
            <ColumnStackVertical
              series={[
                {
                  name: 'Organic Clicks',
                  data: [params.row.percent_organic_clicks],
                  color: '#18A155',
                },
                {
                  name: 'Shopping Clicks',
                  data: [params.row.percent_ad_clicks_from_shopping],
                  color: '#BC9510',
                },
                {
                  name: 'Search Clicks',
                  data: [params.row.percent_ad_clicks_from_search],
                  color: '#F5CE47',
                },
              ].filter((item) => item.data[0] > 0)}
            />
          </PopperCell>
        );
      },
    },
    {
      field: 'clicks_split_compare',
      headerName: t('dashboard.nonBrand.columns.clicksSplitCompare'),
      cellClassName: 'column--data-column compare-column',
      headerClassName: 'compare-column',
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      filterable: false,
      minWidth: 220,
      renderHeader: (params: GridRenderCellParams) => {
        return (
          <LightTooltip title={t('dashboard.nonBrand.tooltips.clicksSplitCompare')}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                {params.colDef.headerName}
              </Typography>
            </Stack>
          </LightTooltip>
        );
      },
      renderCell: (params: GridRenderCellParams) => {
        return (
          <PopperCell
            popperRef={popperRef}
            rowId={params.row.id}
            popper={
              <ClicksSplitTooltip
                percentAdClicksFromSearch={params.row.compare.percent_ad_clicks_from_search}
                percentAdClicksFromShopping={params.row.compare.percent_ad_clicks_from_shopping}
                percentOrganicClicks={params.row.compare.percent_organic_clicks}
              />
            }
          >
            <ColumnStackVertical
              series={[
                {
                  name: 'Organic Clicks',
                  data: [params.row.compare.percent_organic_clicks],
                  color: '#18A155',
                },
                {
                  name: 'Shopping Clicks',
                  data: [params.row.compare.percent_ad_clicks_from_shopping],
                  color: '#BC9510',
                },
                {
                  name: 'Search Clicks',
                  data: [params.row.compare.percent_ad_clicks_from_search],
                  color: '#F5CE47',
                },
              ].filter((item) => item.data[0] > 0)}
            />
          </PopperCell>
        );
      },
    },
    {
      field: 'visibility_rate',
      headerName: t('dashboard.nonBrand.columns.visibilityRate'),
      flex: 1,
      minWidth: 150,
      type: 'number',
      renderHeader: (params: GridRenderCellParams) => {
        return (
          <LightTooltip title={t('dashboard.nonBrand.tooltips.visibilityRate')}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                {params.colDef.headerName}
              </Typography>
            </Stack>
          </LightTooltip>
        );
      },
      renderCell: (params: GridRenderCellParams) => {
        const color =
          colorRange.find(
            (c) => params.row.visibility_rate >= c.min && params.row.visibility_rate <= c.max
          )?.color || 'success.main';
        return (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            py={1}
            sx={{ width: '100%', height: '52px' }}
          >
            <Typography variant="body2">
              {valueOrEmpty(params.row.visibility_rate, formatPercent)}
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              py={1}
              sx={{ width: '100%', height: '52px' }}
            >
              <Stack
                sx={{
                  width: `${params.row.visibility_rate}%`,
                  height: '8px',
                  backgroundColor: color,
                  borderRadius: '6px',
                }}
              />
            </Stack>
          </Stack>
        );
      },
    },
    {
      field: 'visibility_rate_compare',
      headerName: t('dashboard.nonBrand.columns.visibilityRateCompare'),
      cellClassName: 'column--data-column compare-column',
      headerClassName: 'compare-column',
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      filterable: false,
      minWidth: 220,
      renderHeader: (params: GridRenderCellParams) => {
        return (
          <LightTooltip title={t('dashboard.nonBrand.tooltips.visibilityRateCompare')}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                {params.colDef.headerName}
              </Typography>
            </Stack>
          </LightTooltip>
        );
      },
      renderCell: (params: GridRenderCellParams) => {
        const color =
          colorRange.find(
            (c) =>
              params.row.compare.visibility_rate >= c.min &&
              params.row.compare.visibility_rate <= c.max
          )?.color || 'success.main';
        return (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            py={1}
            sx={{ width: '100%', height: '52px' }}
          >
            <Typography variant="body2">
              {valueOrEmpty(params.row.compare.visibility_rate, formatPercent)}
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              py={1}
              sx={{ width: '100%', height: '52px' }}
            >
              <Stack
                sx={{
                  width: `${params.row.compare.visibility_rate}%`,
                  height: '8px',
                  backgroundColor: color,
                  borderRadius: '6px',
                }}
              />
            </Stack>
          </Stack>
        );
      },
    },
    {
      field: 'visibility_rate_diff',
      headerName: t('dashboard.nonBrand.columns.visibilityRateDiff'),
      cellClassName: 'column--data-column compare-column',
      headerClassName: 'compare-column',
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      filterable: false,
      minWidth: 220,
      renderHeader: (params: GridRenderCellParams) => {
        return (
          <LightTooltip title={t('dashboard.nonBrand.tooltips.visibilityRateDiff')}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                {params.colDef.headerName}
              </Typography>
            </Stack>
          </LightTooltip>
        );
      },
      renderCell: (params: GridRenderCellParams) => {
        const color =
          colorRange.find(
            (c) =>
              params.row.compare.visibility_rate >= c.min &&
              params.row.compare.visibility_rate <= c.max
          )?.color || 'success.main';
        return (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            py={1}
            sx={{ width: '100%', height: '52px' }}
          >
            <DiffIcon
              plus={params.row.diff.visibility_rate > 0}
              minus={params.row.diff.visibility_rate < 0}
              good={params.row.diff.visibility_rate > 0}
              bad={params.row.diff.visibility_rate < 0}
              evolution={params.row.diff.visibility_rate}
            />
          </Stack>
        );
      },
    },
    {
      field: 'avg_seo_position',
      headerName: t('dashboard.nonBrand.columns.avgSeoPosition'),
      flex: 1,
      minWidth: 62,
      align: 'left',
      type: 'number',
      renderHeader: (params: GridRenderCellParams) => {
        return (
          <LightTooltip
            title={[
              t('dashboard.nonBrand.tooltips.avgSeoPosition'),
              <a
                href={t('dashboard.nonBrand.tooltips.avgSeoPositionLink')}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('dashboard.nonBrand.tooltips.avgSeoPositionLinkTitle')}
              </a>,
            ]}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                {params.colDef.headerName}
              </Typography>
            </Stack>
          </LightTooltip>
        );
      },
      renderCell: (params: GridRenderCellParams) => {
        const positionValue = params.row.avg_seo_position;
        const position = valueOrEmpty(positionValue, (value: any) => {
          const fixed = value?.toFixed(1);
          return fixed && fixed.endsWith('.0') ? fixed.slice(0, -2) : fixed || '0';
        });
        const change = params.row.seo_position_change;
        const changeText = () => (change > 0 ? `+${change}` : `${change}`);
        const chipColor = positionValue
          ? getSeoPositionChipColor(positionValue)
          : 'rgba(107, 107, 107, 0.5)';

        return (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={'4px'}
            p={0}
            m={0}
          >
            {
              <Chip
                label={position}
                size="small"
                className={`seo-position-chip`}
                sx={{
                  width: 38,
                  height: 28,
                  borderRadius: '6px',
                  fontSize: 14,
                  paddingX: '6px',
                  backgroundColor: chipColor,
                  color: '#212B36',
                }}
              />
            }
            <Typography
              color={change > 0 ? '#18A155' : change < 0 ? '#DC1818' : 'text.secondary'}
              className="cell--avg-seo-position-change"
            >
              {change ? valueOrEmpty(change, changeText) : ''}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'avg_seo_position_compare',
      headerName: t('dashboard.nonBrand.columns.avgSeoPositionCompare'),
      cellClassName: 'compare-column',
      headerClassName: 'compare-column',
      flex: 1,
      minWidth: 62,
      align: 'left',
      type: 'number',
      renderHeader: (params: GridRenderCellParams) => {
        return (
          <LightTooltip title={t('dashboard.nonBrand.tooltips.avgSeoPositionCompare')}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                {params.colDef.headerName} Compare
              </Typography>
            </Stack>
          </LightTooltip>
        );
      },
      renderCell: (params: GridRenderCellParams) => {
        const positionValue = params.row.compare.avg_seo_position;
        const position = valueOrEmpty(positionValue, (value: any) => {
          const fixed = value?.toFixed(1);
          return fixed && fixed.endsWith('.0') ? fixed.slice(0, -2) : fixed || '0';
        });
        const change = params.row.seo_position_change;
        const changeText = () => (change > 0 ? `+${change}` : `${change}`);
        const chipColor = positionValue
          ? getSeoPositionChipColor(positionValue)
          : 'rgba(107, 107, 107, 0.5)';

        return (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={'4px'}
            p={0}
            m={0}
          >
            {
              <Chip
                label={position}
                size="small"
                className={`seo-position-chip`}
                sx={{
                  width: 38,
                  height: 28,
                  borderRadius: '6px',
                  fontSize: 14,
                  paddingX: '6px',
                  backgroundColor: chipColor,
                  color: '#212B36',
                }}
              />
            }
            <Typography
              color={change > 0 ? '#18A155' : change < 0 ? '#DC1818' : 'text.secondary'}
              className="cell--avg-seo-position-change"
            >
              {change ? valueOrEmpty(change, changeText) : ''}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'avg_seo_position_diff',
      headerName: t('dashboard.nonBrand.columns.avgSeoPositionDiff'),
      cellClassName: 'column--data-column compare-column',
      headerClassName: 'compare-column',
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      filterable: false,
      minWidth: 220,
      renderHeader: (params: GridRenderCellParams) => {
        return (
          <LightTooltip title={t('dashboard.nonBrand.tooltips.avgSeoPositionDiff')}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                {params.colDef.headerName}
              </Typography>
            </Stack>
          </LightTooltip>
        );
      },
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            py={1}
            sx={{ width: '100%', height: '52px' }}
          >
            <DiffIcon
              format={(value: number) => value.toFixed(2)}
              plus={params.row.diff.avg_seo_position > 0}
              minus={params.row.diff.avg_seo_position < 0}
              good={params.row.diff.avg_seo_position > 0}
              bad={params.row.diff.avg_seo_position < 0}
              evolution={params.row.diff.avg_seo_position}
            />
          </Stack>
        );
      },
    },
    {
      field: 'paid_search_visibility_rate',
      headerName: t('dashboard.nonBrand.columns.paidSearchShare'),
      cellClassName: 'column--highlighblue',
      headerClassName: 'column--highlighblue',
      flex: 1,
      minWidth: 170,
      type: 'number',
      renderHeader: (params: GridRenderCellParams) => {
        return (
          <LightTooltip title={t('dashboard.nonBrand.tooltips.paidSearchShare')}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                {params.colDef.headerName}
              </Typography>
            </Stack>
          </LightTooltip>
        );
      },
      renderCell: (params: GridRenderCellParams) => {
        const value = params.row.paid_search_visibility_rate || 0;

        if (!value) {
          return (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="left"
              spacing={1}
              sx={{ width: '100%', minHeight: '50px' }}
            >
              <Stack direction="column" alignItems="center" justifyContent="center" spacing={1.5}>
                <Typography variant="body2">-</Typography>
              </Stack>
            </Stack>
          );
        }

        // Get breakdown values for the 3 segments
        const firstSearchRate = params.row.first_search_visibility_rate;
        const topSearchRate = params.row.top_search_visibility_rate;
        const competSearchRate = params.row.compet_search_visibility_rate;

        // Calculate percentages for each segment
        const firstPercent = Math.min(Math.max(firstSearchRate, 0), 100);
        const topPercent = Math.min(Math.max(topSearchRate, 0), 100);
        const competPercent = Math.min(Math.max(competSearchRate, 0), 100);

        // Calculate total for proportional display
        const total = firstPercent + topPercent + competPercent;
        const firstBarWidth = total > 0 ? (firstPercent / total) * 100 : 0;
        const topBarWidth = total > 0 ? (topPercent / total) * 100 : 0;
        const competBarWidth = total > 0 ? (competPercent / total) * 100 : 0;
        return (
          <PopperCell
            popperRef={popperRef}
            rowId={params.row.id}
            popper={
              <PaidSearchShareTooltip
                firstSearchVisibilityRate={params.row.first_search_visibility_rate}
                topSearchVisibilityRate={params.row.top_search_visibility_rate}
                competSearchVisibilityRate={params.row.compet_search_visibility_rate}
              />
            }
          >
            <Stack direction="column" alignItems="center" justifyContent="center" spacing={1.5}>
              <Typography
                variant="body2"
                sx={{ minWidth: 40, color: '#212B36', textAlign: 'justify' }}
              >
                {valueOrEmpty(value, (value: any) => value?.toFixed(0) + '%')}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} flexGrow={1} sx={{ height: 12 }}>
              {/* 1-3 position segment (bright yellow) */}
              {firstBarWidth > 0 && (
                <Stack
                  sx={{
                    height: 12,
                    flex: firstBarWidth,
                    backgroundColor: '#DEBC0F',
                    borderRadius: '2px',
                    minWidth: firstBarWidth > 0 ? '2px' : 0,
                  }}
                />
              )}
              {/* 4-10 position segment (lighter yellow) */}
              {topBarWidth > 0 && (
                <Stack
                  sx={{
                    height: 12,
                    flex: topBarWidth,
                    backgroundColor: '#F3E300',
                    borderRadius: '2px',
                    minWidth: topBarWidth > 0 ? '2px' : 0,
                  }}
                />
              )}
              {/* Competitors segment (grey) */}
              {competBarWidth > 0 && (
                <Stack
                  sx={{
                    height: 12,
                    flex: competBarWidth,
                    backgroundColor: '#637381',
                    borderRadius: '2px',
                    minWidth: competBarWidth > 0 ? '2px' : 0,
                  }}
                />
              )}
            </Stack>
          </PopperCell>
        );
      },
    },
    {
      field: 'paid_search_visibility_rate_compare',
      headerName: t('dashboard.nonBrand.columns.paidSearchShareCompare'),
      cellClassName: 'column--highlighblue compare-column',
      headerClassName: 'column--highlighblue compare-column',
      flex: 1,
      minWidth: 170,
      type: 'number',
      renderHeader: (params: GridRenderCellParams) => {
        return (
          <LightTooltip title={t('dashboard.nonBrand.tooltips.paidSearchShareCompare')}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                {params.colDef.headerName}
              </Typography>
            </Stack>
          </LightTooltip>
        );
      },
      renderCell: (params: GridRenderCellParams) => {
        const value = params.row.compare.paid_search_visibility_rate || 0;

        if (!value) {
          return (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="left"
              spacing={1}
              sx={{ width: '100%', minHeight: '50px' }}
            >
              <Stack direction="column" alignItems="center" justifyContent="center" spacing={1.5}>
                <Typography variant="body2">-</Typography>
              </Stack>
            </Stack>
          );
        }

        // Get breakdown values for the 3 segments
        const firstSearchRate = params.row.compare.first_search_visibility_rate;
        const topSearchRate = params.row.compare.top_search_visibility_rate;
        const competSearchRate = params.row.compare.compet_search_visibility_rate;

        // Calculate percentages for each segment
        const firstPercent = Math.min(Math.max(firstSearchRate, 0), 100);
        const topPercent = Math.min(Math.max(topSearchRate, 0), 100);
        const competPercent = Math.min(Math.max(competSearchRate, 0), 100);

        // Calculate total for proportional display
        const total = firstPercent + topPercent + competPercent;
        const firstBarWidth = total > 0 ? (firstPercent / total) * 100 : 0;
        const topBarWidth = total > 0 ? (topPercent / total) * 100 : 0;
        const competBarWidth = total > 0 ? (competPercent / total) * 100 : 0;
        return (
          <PopperCell
            popperRef={popperRef}
            rowId={params.row.id}
            popper={
              <PaidSearchShareTooltip
                firstSearchVisibilityRate={params.row.compare.first_search_visibility_rate}
                topSearchVisibilityRate={params.row.compare.top_search_visibility_rate}
                competSearchVisibilityRate={params.row.compare.compet_search_visibility_rate}
              />
            }
          >
            <Stack direction="column" alignItems="center" justifyContent="center" spacing={1.5}>
              <Typography
                variant="body2"
                sx={{ minWidth: 40, color: '#212B36', textAlign: 'justify' }}
              >
                {valueOrEmpty(value, (value: any) => value?.toFixed(0) + '%')}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} flexGrow={1} sx={{ height: 12 }}>
              {/* 1-3 position segment (bright yellow) */}
              {firstBarWidth > 0 && (
                <Stack
                  sx={{
                    height: 12,
                    flex: firstBarWidth,
                    backgroundColor: '#DEBC0F',
                    borderRadius: '2px',
                    minWidth: firstBarWidth > 0 ? '2px' : 0,
                  }}
                />
              )}
              {/* 4-10 position segment (lighter yellow) */}
              {topBarWidth > 0 && (
                <Stack
                  sx={{
                    height: 12,
                    flex: topBarWidth,
                    backgroundColor: '#F3E300',
                    borderRadius: '2px',
                    minWidth: topBarWidth > 0 ? '2px' : 0,
                  }}
                />
              )}
              {/* Competitors segment (grey) */}
              {competBarWidth > 0 && (
                <Stack
                  sx={{
                    height: 12,
                    flex: competBarWidth,
                    backgroundColor: '#637381',
                    borderRadius: '2px',
                    minWidth: competBarWidth > 0 ? '2px' : 0,
                  }}
                />
              )}
            </Stack>
          </PopperCell>
        );
      },
    },
    {
      field: 'paid_shopping_visibility_rate',
      headerName: t('dashboard.nonBrand.columns.paidShoppingShare'),
      cellClassName: 'column--highlighpurple',
      headerClassName: 'column--highlighpurple',
      flex: 1,
      minWidth: 170,
      type: 'number',
      renderCell: (params: GridRenderCellParams) => {
        const value = params.row.paid_shopping_visibility_rate || 0;

        if (!value) {
          return (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="left"
              spacing={1}
              sx={{ width: '100%', minHeight: '50px' }}
            >
              <Typography variant="body2">-</Typography>
            </Stack>
          );
        }

        // Get breakdown values for the 3 segments
        const lastShoppingRate = params.row.last_shopping_visibility_rate;
        const topShoppingRate = params.row.top_shopping_visibility_rate;
        const competShoppingRate = params.row.compet_shopping_visibility_rate;

        // Calculate percentages for each segment
        const lastPercent = Math.min(Math.max(lastShoppingRate, 0), 100);
        const topPercent = Math.min(Math.max(topShoppingRate, 0), 100);
        const competPercent = Math.min(Math.max(competShoppingRate, 0), 100);

        // Calculate total for proportional display
        const total = lastPercent + topPercent + competPercent;
        const lastBarWidth = total > 0 ? (lastPercent / total) * 100 : 0;
        const topBarWidth = total > 0 ? (topPercent / total) * 100 : 0;
        const competBarWidth = total > 0 ? (competPercent / total) * 100 : 0;

        return (
          <PopperCell
            popperRef={popperRef}
            rowId={params.row.id}
            popper={
              <PaidShoppingShareTooltip
                lastShoppingVisibilityRate={params.row.last_shopping_visibility_rate}
                topShoppingVisibilityRate={params.row.top_shopping_visibility_rate}
                competShoppingVisibilityRate={params.row.compet_shopping_visibility_rate}
              />
            }
          >
            <Typography
              variant="body2"
              sx={{ minWidth: 40, color: '#212B36', textAlign: 'justify' }}
            >
              {valueOrEmpty(value, formatPercent)}
            </Typography>
            <Stack direction="row" spacing={0.5} flexGrow={1} sx={{ height: 12 }}>
              {/* 1st position segment (orange) */}
              {lastBarWidth > 0 && (
                <Stack
                  sx={{
                    height: 12,
                    flex: lastBarWidth,
                    backgroundColor: '#FF9A0B',
                    borderRadius: '2px',
                    minWidth: lastBarWidth > 0 ? '2px' : 0,
                  }}
                />
              )}
              {/* 2-3 position segment (lighter orange) */}
              {topBarWidth > 0 && (
                <Stack
                  sx={{
                    height: 12,
                    flex: topBarWidth,
                    backgroundColor: '#FFD89F',
                    borderRadius: '2px',
                    minWidth: topBarWidth > 0 ? '2px' : 0,
                  }}
                />
              )}
              {/* Competitors segment (grey) */}
              {competBarWidth > 0 && (
                <Stack
                  sx={{
                    height: 12,
                    flex: competBarWidth,
                    backgroundColor: '#D0D0D0',
                    borderRadius: '2px',
                    minWidth: competBarWidth > 0 ? '2px' : 0,
                  }}
                />
              )}
            </Stack>
          </PopperCell>
        );
      },
    },
    {
      field: 'paid_shopping_visibility_rate_compare',
      headerName: t('dashboard.nonBrand.columns.paidShoppingShareCompare'),
      cellClassName: 'column--highlighpurple compare-column',
      headerClassName: 'column--highlighpurple compare-column',
      flex: 1,
      minWidth: 170,
      type: 'number',
      renderCell: (params: GridRenderCellParams) => {
        const value = params.row.compare.paid_shopping_visibility_rate || 0;

        if (!value) {
          return (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="left"
              spacing={1}
              sx={{ width: '100%', minHeight: '50px' }}
            >
              <Typography variant="body2">-</Typography>
            </Stack>
          );
        }

        // Get breakdown values for the 3 segments
        const lastShoppingRate = params.row.compare.last_shopping_visibility_rate;
        const topShoppingRate = params.row.compare.top_shopping_visibility_rate;
        const competShoppingRate = params.row.compare.compet_shopping_visibility_rate;

        // Calculate percentages for each segment
        const lastPercent = Math.min(Math.max(lastShoppingRate, 0), 100);
        const topPercent = Math.min(Math.max(topShoppingRate, 0), 100);
        const competPercent = Math.min(Math.max(competShoppingRate, 0), 100);

        // Calculate total for proportional display
        const total = lastPercent + topPercent + competPercent;
        const lastBarWidth = total > 0 ? (lastPercent / total) * 100 : 0;
        const topBarWidth = total > 0 ? (topPercent / total) * 100 : 0;
        const competBarWidth = total > 0 ? (competPercent / total) * 100 : 0;

        return (
          <PopperCell
            popperRef={popperRef}
            rowId={params.row.id}
            popper={
              <PaidShoppingShareTooltip
                lastShoppingVisibilityRate={params.row.compare.last_shopping_visibility_rate}
                topShoppingVisibilityRate={params.row.compare.top_shopping_visibility_rate}
                competShoppingVisibilityRate={params.row.compare.compet_shopping_visibility_rate}
              />
            }
          >
            <Typography
              variant="body2"
              sx={{ minWidth: 40, color: '#212B36', textAlign: 'justify' }}
            >
              {valueOrEmpty(value, formatPercent)}
            </Typography>
            <Stack direction="row" spacing={0.5} flexGrow={1} sx={{ height: 12 }}>
              {/* 1st position segment (orange) */}
              {lastBarWidth > 0 && (
                <Stack
                  sx={{
                    height: 12,
                    flex: lastBarWidth,
                    backgroundColor: '#FF9A0B',
                    borderRadius: '2px',
                    minWidth: lastBarWidth > 0 ? '2px' : 0,
                  }}
                />
              )}
              {/* 2-3 position segment (lighter orange) */}
              {topBarWidth > 0 && (
                <Stack
                  sx={{
                    height: 12,
                    flex: topBarWidth,
                    backgroundColor: '#FFD89F',
                    borderRadius: '2px',
                    minWidth: topBarWidth > 0 ? '2px' : 0,
                  }}
                />
              )}
              {/* Competitors segment (grey) */}
              {competBarWidth > 0 && (
                <Stack
                  sx={{
                    height: 12,
                    flex: competBarWidth,
                    backgroundColor: '#D0D0D0',
                    borderRadius: '2px',
                    minWidth: competBarWidth > 0 ? '2px' : 0,
                  }}
                />
              )}
            </Stack>
          </PopperCell>
        );
      },
    },
    {
      field: 'top_comp',
      headerName: t('dashboard.nonBrand.columns.topCompetitors'),
      flex: 1,
      disableColumnMenu: true,
      minWidth: 120,
      renderHeader: (params: GridRenderCellParams) => (
        <LightTooltip title={t('dashboard.nonBrand.tooltips.topCompetitors')}>
          <Stack direction="row" alignItems="center" justifyContent="space-around" spacing={1}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
              {params.colDef.headerName}
            </Typography>
          </Stack>
        </LightTooltip>
      ),
      renderCell: (params: GridRenderCellParams) => {
        if (!params.row.top_compet__process?.length) {
          return (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="left"
              sx={{ width: '100%', minHeight: '50px' }}
            >
              <Typography variant="body2">-</Typography>
            </Stack>
          );
        }

        return (
          <PopperCell
            popperRef={popperRef}
            rowId={params.row.id}
            popper={
              <CompetitorsTooltip
                title={
                  topCompetField === 'top5_compet_total'
                    ? t('dashboard.nonBrand.columnGroups.visibilityRate')
                    : topCompetField === 'top5_organic_rate'
                      ? t('dashboard.nonBrand.columnGroups.visibilityRate')
                      : topCompetField === 'top5_search_rate'
                        ? t('dashboard.nonBrand.columnGroups.paidSearchPresenceRate')
                        : t('dashboard.nonBrand.columnGroups.shoppingPresenceRate')
                }
                competitors={params.row.top_compet__process}
              />
            }
          >
            <AvatarGroup
              max={4}
              sx={{
                '& .MuiAvatar-root': {
                  border: 'none',
                  width: 24,
                  height: 24,
                },
              }}
            >
              {[...(params.row.top_compet__process || [])]
                ?.filter((c: any) => c.rate_field > 0)
                .map((c: any, i: number) => (
                  <Avatar
                    key={i}
                    alt={c.domain_name}
                    src={`https://www.google.com/s2/favicons?domain=${c.domain_name}&sz=32`}
                  />
                ))}
            </AvatarGroup>
          </PopperCell>
        );
      },
    },
    {
      field: 'top_comp_compare',
      headerName: t('dashboard.nonBrand.columns.topCompetitorsCompare'),
      cellClassName: 'compare-column',
      headerClassName: 'compare-column',
      flex: 1,
      disableColumnMenu: true,
      minWidth: 120,
      renderHeader: (params: GridRenderCellParams) => (
        <Stack direction="row" alignItems="center" justifyContent="space-around" spacing={1}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
            {params.colDef.headerName}
          </Typography>
        </Stack>
      ),
      renderCell: (params: GridRenderCellParams) => {
        if (!params.row.compare.top_compet__process?.length) {
          return (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="left"
              sx={{ width: '100%', minHeight: '50px' }}
            >
              <Typography variant="body2">-</Typography>
            </Stack>
          );
        }

        return (
          <PopperCell
            popperRef={popperRef}
            rowId={params.row.id}
            popper={
              <CompetitorsTooltip
                title={
                  topCompetField === 'top5_compet_total'
                    ? t('dashboard.nonBrand.columnGroups.visibilityRate')
                    : topCompetField === 'top5_search_rate'
                      ? t('dashboard.nonBrand.columnGroups.visibilityRate')
                      : topCompetField === 'top5_organic_rate'
                        ? t('dashboard.nonBrand.columnGroups.paidSearchPresenceRate')
                        : t('dashboard.nonBrand.columnGroups.shoppingPresenceRate')
                }
                competitors={params.row.compare.top_compet__process}
              />
            }
          >
            <AvatarGroup
              max={4}
              sx={{
                '& .MuiAvatar-root': {
                  border: 'none',
                  width: 24,
                  height: 24,
                },
              }}
            >
              {[...(params.row.compare.top_compet__process || [])]
                ?.sort((a: any, b: any) => b.total_rate - a.total_rate)
                .map((c: any, i: number) => (
                  <Avatar
                    key={i}
                    alt={c.domain_name}
                    src={`https://www.google.com/s2/favicons?domain=${c.domain_name}&sz=32`}
                  />
                ))}
            </AvatarGroup>
          </PopperCell>
        );
      },
    },
    {
      field: 'ad_cost',
      headerName: t('dashboard.nonBrand.columns.adCost'),
      flex: 1,
      minWidth: 75,
      type: 'number',
      renderHeader: (params: GridRenderCellParams) => (
        <LightTooltip title={t('dashboard.nonBrand.tooltips.adCost')}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
              {params.colDef.headerName}
            </Typography>
          </Stack>
        </LightTooltip>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="left"
          spacing={0.5}
          py={1}
          sx={{ width: '100%' }}
        >
          <Typography variant="body2">
            {valueOrEmpty(params.row.ad_cost, formatToCurrency)}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'ad_cost_compare',
      headerName: t('dashboard.nonBrand.columns.adCostCompare'),
      cellClassName: 'compare-column',
      headerClassName: 'compare-column',
      flex: 1,
      minWidth: 75,
      type: 'number',
      renderHeader: (params: GridRenderCellParams) => (
        <LightTooltip title={t('dashboard.nonBrand.tooltips.adCostCompare')}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
              {params.colDef.headerName}
            </Typography>
          </Stack>
        </LightTooltip>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="left"
          spacing={0.5}
          py={1}
          sx={{ width: '100%' }}
        >
          <Typography variant="body2">
            {valueOrEmpty(params.row.compare.ad_cost, formatToCurrency)}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'ad_cost_diff',
      headerName: t('dashboard.nonBrand.columns.adCostDiff'),
      cellClassName: 'compare-column',
      headerClassName: 'compare-column',
      flex: 1,
      minWidth: 75,
      type: 'number',
      renderHeader: (params: GridRenderCellParams) => (
        <LightTooltip title={t('dashboard.nonBrand.tooltips.adCostDiff')}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
              {params.colDef.headerName}
            </Typography>
          </Stack>
        </LightTooltip>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <DiffIcon
          format={(value: number) => formatNumber(value.toFixed(2))}
          plus={params.row.diff.ad_cost > 0}
          minus={params.row.diff.ad_cost < 0}
          good={params.row.diff.ad_cost < 0}
          bad={params.row.diff.ad_cost > 0}
          evolution={params.row.diff.ad_cost}
        />
      ),
    },
    {
      field: 'ad_cpc',
      headerName: t('dashboard.nonBrand.columns.paidCpc'),
      flex: 1,
      minWidth: 75,
      type: 'number',
      renderHeader: (params: GridRenderCellParams) => (
        <LightTooltip title={t('dashboard.nonBrand.tooltips.paidCpc')}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
              {params.colDef.headerName}
            </Typography>
          </Stack>
        </LightTooltip>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="left"
          spacing={0.5}
          py={1}
          sx={{ width: '100%' }}
        >
          <Typography variant="body2">
            {valueOrEmpty(params.row.ad_cpc, formatToCurrency.bind(null, params.row.ad_cpc, 2))}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'ad_cpc_compare',
      headerName: t('dashboard.nonBrand.columns.paidCpcCompare'),
      cellClassName: 'compare-column',
      headerClassName: 'compare-column',
      flex: 1,
      minWidth: 75,
      type: 'number',
      renderHeader: (params: GridRenderCellParams) => (
        <LightTooltip title={t('dashboard.nonBrand.tooltips.paidCpcCompare')}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
              {params.colDef.headerName}
            </Typography>
          </Stack>
        </LightTooltip>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="left"
          spacing={0.5}
          py={1}
          sx={{ width: '100%' }}
        >
          <Typography variant="body2">
            {valueOrEmpty(
              params.row.compare.ad_cpc,
              formatToCurrency.bind(null, params.row.compare.ad_cpc, 2)
            )}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'ad_cpc_diff',
      headerName: t('dashboard.nonBrand.columns.paidCpcDiff'),
      cellClassName: 'compare-column',
      headerClassName: 'compare-column',
      flex: 1,
      minWidth: 75,
      type: 'number',
      renderHeader: (params: GridRenderCellParams) => (
        <LightTooltip title={t('dashboard.nonBrand.tooltips.paidCpcDiff')}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
              {params.colDef.headerName}
            </Typography>
          </Stack>
        </LightTooltip>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <DiffIcon
          format={(value: number) => value.toFixed(2)}
          plus={params.row.diff.ad_cpc > 0}
          minus={params.row.diff.ad_cpc < 0}
          good={params.row.diff.ad_cpc < 0}
          bad={params.row.diff.ad_cpc > 0}
          evolution={params.row.diff.ad_cpc}
        />
      ),
    },
    {
      field: 'ad_conversions',
      headerName: t('dashboard.nonBrand.columns.adConv'),
      flex: 1,
      minWidth: 100,
      type: 'number',
      renderHeader: (params: GridRenderCellParams) => (
        <LightTooltip title={t('dashboard.nonBrand.tooltips.adConv')}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
              {params.colDef.headerName}
            </Typography>
          </Stack>
        </LightTooltip>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="left"
          spacing={0.5}
          py={1}
          sx={{ width: '100%' }}
        >
          <Typography variant="body2">
            {valueOrEmpty(params.row.ad_conversions, fNumber)}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'ad_conversions_compare',
      headerName: t('dashboard.nonBrand.columns.adConvCompare'),
      cellClassName: 'compare-column',
      headerClassName: 'compare-column',
      flex: 1,
      minWidth: 100,
      type: 'number',
      renderHeader: (params: GridRenderCellParams) => (
        <LightTooltip title={t('dashboard.nonBrand.tooltips.adConvCompare')}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
              {params.colDef.headerName}
            </Typography>
          </Stack>
        </LightTooltip>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="left"
          spacing={0.5}
          py={1}
          sx={{ width: '100%' }}
        >
          <Typography variant="body2">
            {valueOrEmpty(params.row.compare.ad_conversions, fNumber)}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'ad_conversions_diff',
      headerName: t('dashboard.nonBrand.columns.adConvDiff'),
      cellClassName: 'compare-column',
      headerClassName: 'compare-column',
      flex: 1,
      minWidth: 100,
      type: 'number',
      renderHeader: (params: GridRenderCellParams) => (
        <LightTooltip title={t('dashboard.nonBrand.tooltips.adConvDiff')}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
              {params.colDef.headerName}
            </Typography>
          </Stack>
        </LightTooltip>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <DiffIcon
          format={(value: number) => formatNumber(value)}
          plus={params.row.diff.ad_conversions > 0}
          minus={params.row.diff.ad_conversions < 0}
          good={params.row.diff.ad_conversions > 0}
          bad={params.row.diff.ad_conversions < 0}
          evolution={params.row.diff.ad_conversions}
        />
      ),
    },

    {
      field: 'ROAS',
      headerName:
        paidRoasField === 'paid_roas'
          ? t('dashboard.nonBrand.columns.paidRoas')
          : t('dashboard.nonBrand.columns.paidCpa'),
      flex: 1,
      minWidth: 100,
      type: 'number',
      renderHeader: (params: GridRenderCellParams) => (
        <LightTooltip
          title={
            paidRoasField === 'paid_roas'
              ? t('dashboard.nonBrand.tooltips.paidRoas')
              : t('dashboard.nonBrand.tooltips.paidCpa')
          }
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
              {params.colDef.headerName}
            </Typography>
          </Stack>
        </LightTooltip>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="left"
          spacing={0.5}
          py={1}
          sx={{ width: '100%' }}
        >
          <Typography variant="body2">
            {valueOrEmpty(
              params.row[paidRoasField as keyof typeof params.row],
              paidRoasField === 'paid_roas'
                ? formatPercent
                : formatToCurrency.bind(
                    null,
                    params.row[paidRoasField as keyof typeof params.row],
                    2
                  )
            )}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'ROAS_compare',
      headerName:
        paidRoasField === 'paid_roas'
          ? t('dashboard.nonBrand.columns.paidRoasCompare')
          : t('dashboard.nonBrand.columns.paidCpaCompare'),
      cellClassName: 'compare-column',
      headerClassName: 'compare-column',
      flex: 1,
      minWidth: 100,
      type: 'number',
      renderHeader: (params: GridRenderCellParams) => (
        <LightTooltip title={t('dashboard.nonBrand.tooltips.paidRoasCompare')}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
              {params.colDef.headerName}
            </Typography>
          </Stack>
        </LightTooltip>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="left"
          spacing={0.5}
          py={1}
          sx={{ width: '100%' }}
        >
          <Typography variant="body2">
            {valueOrEmpty(
              params.row.compare[paidRoasField as keyof typeof params.row],
              paidRoasField === 'paid_roas'
                ? formatPercent
                : formatToCurrency.bind(
                    null,
                    params.row.compare[paidRoasField as keyof typeof params.row],
                    2
                  )
            )}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'opportunity',
      headerName: t('dashboard.nonBrand.columns.opportunity'),
      flex: 1,
      minWidth: 120,
      type: 'string',
      renderHeader: (params: GridRenderCellParams) => (
        <LightTooltip title={t('dashboard.nonBrand.tooltips.opportunity')}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
              {params.colDef.headerName}
            </Typography>
          </Stack>
        </LightTooltip>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const opportunityKey = params.row.opportunity.toLowerCase().replace(/ /g, '');
        const opportunityMap: Record<string, string> = {
          no_opportunity: t('dashboard.nonBrand.opportunities.noOpportunity'),
          overlap: t('dashboard.nonBrand.opportunities.overlap'),
          seo_boost: t('dashboard.nonBrand.opportunities.organicBoost'),
          sea_boost: t('dashboard.nonBrand.opportunities.cpcBoost'),
        };
        const label =
          opportunityMap[opportunityKey.toLowerCase()] ||
          params.row.opportunity.charAt(0).toUpperCase() + params.row.opportunity.slice(1);
        return (
          <Chip
            label={label}
            className={`opportunity-chip opportunity-chip-${params.row.opportunity}`}
            size="small"
          />
        );
      },
    },
  ].filter((column) => {
    if (compare) {
      return true;
    }
    return !['compare', 'diff'].some((suffix) => column.field.endsWith(suffix));
  }) as GridColDef[];
};

type FilterOperator =
  | 'lt'
  | 'lte'
  | 'gt'
  | 'gte'
  | 'equal'
  | 'notEqual'
  | 'between'
  | 'contains'
  | 'doesNotContain'
  | 'startsWith'
  | 'endsWith'
  | 'isEmpty'
  | 'isNotEmpty'
  | 'isAnyOf';

interface ColumnFilter {
  id: string; // Unique ID for each filter
  field: string;
  operator: FilterOperator;
  value: string;
  value2?: string; // For 'between' operator
  values?: string[]; // For 'isAnyOf' operator
}

export default function SearchTermsGrid() {
  const { t } = useLocales();
  const workspace = useCurrentWorkspace();
  const { selectedRegion } = useNonBrandedContext();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 10,
    page: 0,
  });
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'search_term', sort: 'desc' },
  ]);
  const { searchTermsGrid, isLoading, isError, isFetching } = useSearchTermsGridData(
    Object.assign(
      {},
      { ...filterModel, items: filterModel.items.filter((item) => !!item.value) },
      paginationModel,
      { countries: selectedRegion ? [selectedRegion] : [] }
    ),
    sortModel
  );
  const [topCompetField, setTopCompetField] = useState<string>('top5_organic_rate');
  const [top_compet_rate_field, setTopCompetRateField] = useState<string>('organic_rate');
  const opportunityMap: Record<string, string> = {
    no_opportunity: t('dashboard.nonBrand.opportunities.noOpportunity'),
    overlap: t('dashboard.nonBrand.opportunities.overlap'),
    seo_boost: t('dashboard.nonBrand.opportunities.organicBoost'),
    sea_boost: t('dashboard.nonBrand.opportunities.cpcBoost'),
  };

  const [paidRoasField, setPaidRoasField] = useState<string>(
    workspace?.template?.toLowerCase() === 'e-commerce' ? 'paid_roas' : 'paid_cpa'
  );

  useEffect(() => {
    setPaidRoasField(
      workspace?.template?.toLowerCase() === 'e-commerce' ? 'paid_roas' : 'paid_cpa'
    );
  }, [workspace]);

  const apiRef = useRef<any>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  // Initialize column visibility model - all columns visible by default (empty object means all visible)
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});
  const {
    selectedOpportunity: selectedOpportunityContext,
    setSelectedOpportunity: setSelectedOpportunityContext,
  } = useNonBrandedContext();
  const searchTermGridData = useMemo(() => {
    return searchTermsGrid?.data?.map((item: any) => ({
      ...item,
      top_compet__process: item[topCompetField].map((competitor: any) => ({
        ...competitor,
        rate_field: competitor[top_compet_rate_field],
      })),
      compare: {
        ...item.compare,
        top_compet__process: item.compare[topCompetField].map((competitor: any) => ({
          ...competitor,
          rate_field: competitor[top_compet_rate_field],
        })),
      },
    }));
  }, [searchTermsGrid, topCompetField, top_compet_rate_field]);

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    setPaginationModel(model);
  };

  const columns = useColumns(paidRoasField, topCompetField);

  const handleTopCompetFieldChange = (field: string, rateField: string) => {
    if (field === topCompetField) {
      return;
    }
    setTopCompetField(field);
    setTopCompetRateField(rateField);
  };

  useEffect(() => {
    setColumnVisibilityModel((prevModel) => ({
      ...prevModel,
      paid_shopping_visibility_rate: searchTermGridData?.every(
        (item: any) => (item.paid_shopping_visibility_rate || 0) === 0
      )
        ? false
        : true,
      paid_shopping_visibility_rate_compare: searchTermGridData?.every(
        (item: any) => (item.compare.paid_shopping_visibility_rate || 0) === 0
      )
        ? false
        : true,
    }));
  }, [searchTermGridData]);

  const columnGroupingModel: GridColumnGroupingModel = [
    {
      groupId: 'actions',
      children: [{ field: 'top_comp' }, { field: 'top_comp_compare' }],
      renderHeaderGroup() {
        return (
          <Stack direction="row" alignItems="center" justifyContent="space-around" spacing={0}>
            <LightTooltip title={t('dashboard.nonBrand.columnGroups.visibilityRate')}>
              <CategoryButton
                onClick={() => handleTopCompetFieldChange('top5_organic_rate', 'organic_rate')}
                active={topCompetField === 'top5_organic_rate'}
              >
                <Iconify icon="eva:search-fill" width={18} />
              </CategoryButton>
            </LightTooltip>
            <LightTooltip title={t('dashboard.nonBrand.columnGroups.paidSearchPresenceRate')}>
              <CategoryButton
                onClick={() => handleTopCompetFieldChange('top5_search_rate', 'search_rate')}
                active={topCompetField === 'top5_search_rate'}
              >
                <SvgIcon component={TextIcon} />
              </CategoryButton>
            </LightTooltip>
            <LightTooltip title={t('dashboard.nonBrand.columnGroups.shoppingPresenceRate')}>
              <CategoryButton
                onClick={() => handleTopCompetFieldChange('top5_shopping_rate', 'shopping_rate')}
                active={topCompetField === 'top5_shopping_rate'}
              >
                <SvgIcon component={ShoppingCartIcon} />
              </CategoryButton>
            </LightTooltip>
          </Stack>
        );
      },
    },
    {
      groupId: 'roasGroup',
      children: [{ field: 'ROAS' }, { field: 'ROAS_compare' }],
      headerAlign: 'center',
      renderHeaderGroup() {
        return (
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={0}>
            <LightTooltip title={t('dashboard.nonBrand.columnGroups.paidCpa')}>
              <CategoryButton
                onClick={() => {
                  setPaidRoasField('paid_cpa');
                }}
                active={paidRoasField === 'paid_cpa'}
              >
                <SvgIcon component={TextIcon} />
              </CategoryButton>
            </LightTooltip>
            <LightTooltip title={t('dashboard.nonBrand.columnGroups.paidRoas')}>
              <CategoryButton
                onClick={() => {
                  setPaidRoasField('paid_roas');
                }}
                active={paidRoasField === 'paid_roas'}
              >
                <SvgIcon component={ShoppingCartIcon} />
              </CategoryButton>
            </LightTooltip>
          </Stack>
        );
      },
    },
  ];

  const Toolbar = (props: any) => {
    return (
      <Stack
        ref={toolbarRef}
        direction="row"
        alignItems="center"
        spacing={0}
        px={2.5}
        py={1.25}
        sx={{
          borderBottom: '1px solid rgba(145, 158, 171, 0.12)',
          minHeight: 46,
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: '#333C47',
            textAlign: 'center',
            fontFamily: 'Public Sans',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: '120%',
          }}
        >
          {t('dashboard.nonBrand.searchTerms')}
        </Typography>
        <Box flexGrow={1} />
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Autocomplete
            size="small"
            options={[
              { label: t('dashboard.nonBrand.opportunities.overlap'), value: 'overlap' },
              { label: t('dashboard.nonBrand.opportunities.organicBoost'), value: 'seo_boost' },
              { label: t('dashboard.nonBrand.opportunities.cpcBoost'), value: 'sea_boost' },
              {
                label: t('dashboard.nonBrand.opportunities.noOpportunity'),
                value: 'no_opportunity',
              },
            ]}
            onChange={(event, newValue) =>
              setSelectedOpportunityContext(newValue?.value || (null as any))
            }
            fullWidth
            multiple={false}
            value={
              selectedOpportunityContext
                ? { label: selectedOpportunityContext, value: selectedOpportunityContext }
                : null
            }
            renderOption={(props, option) => (
              <li {...props}>
                <Chip
                  className={`opportunity-chip opportunity-chip-${option.value}`}
                  label={option.label}
                  size="small"
                  variant="soft"
                />
              </li>
            )}
            renderInput={(params) => (
              <>
                <TextField
                  {...params}
                  value={''}
                  inputProps={{ ...params.inputProps, value: '' }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: undefined,
                    startAdornment: selectedOpportunityContext ? (
                      <InputAdornment position="start">
                        <Chip
                          className={`opportunity-chip opportunity-chip-${selectedOpportunityContext}`}
                          label={opportunityMap[selectedOpportunityContext]}
                          size="small"
                          variant="soft"
                          onDelete={() => setSelectedOpportunityContext(null as any)}
                        />
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{ width: 150 }}
                  size="small"
                  placeholder={
                    selectedOpportunityContext
                      ? undefined
                      : t('dashboard.nonBrand.columns.opportunity')
                  }
                />
              </>
            )}
          />
        </Stack>
        <Stack direction="row" alignItems="center" px={1} spacing={0.5}>
          <IconButton
            onClick={() => apiRef.current?.showPreferences('columns')}
            sx={{
              width: 26,
              height: 26,
              padding: 0,
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Iconify icon="mingcute:calendar-week-line" width={18} height={18} />
          </IconButton>
          <IconButton
            onClick={() => apiRef.current?.showFilterPanel()}
            sx={{
              width: 26,
              height: 26,
              padding: 0,
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Iconify icon="ic:round-filter-list" width={18} height={18} />
          </IconButton>
          <IconButton
            sx={{
              width: 24,
              height: 24,
              padding: 0,
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Iconify icon="lets-icons:export" width={16} height={16} />
          </IconButton>
        </Stack>
      </Stack>
    );
  };

  return (
    <Card
      sx={{
        borderRadius: 2,
        border: '1px solid rgba(145, 158, 171, 0.12)',
        overflow: 'hidden',
      }}
    >
      <CardContent
        sx={{
          px: 0,
          py: 0,
          overflow: 'visible',
          minHeight: isLoading || isFetching ? 600 : 'auto',
        }}
      >
        <DataGridPro
          classes={{
            root: 'search-term-grid',
            columnHeaders: 'search-term-grid-column-headers',
            columnHeader: 'search-term-grid-column-header',
          }}
          apiRef={apiRef}
          pinnedColumns={{ left: ['search_term'], right: ['opportunity', 'opportunity_compare'] }}
          loading={isLoading || isFetching}
          rows={isLoading || isFetching ? [] : searchTermGridData}
          columns={columns}
          getRowHeight={() => 60}
          disableColumnFilter={true}
          disableColumnMenu={true}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          filterModel={filterModel}
          filterMode="server"
          sortingMode="server"
          disableColumnSelector={true}
          disableMultipleColumnsSorting={true}
          onSortModelChange={setSortModel}
          sortModel={sortModel}
          onFilterModelChange={setFilterModel}
          disableDensitySelector
          checkboxSelection={false}
          pagination
          autoHeight
          disableRowSelectionOnClick={true}
          paginationMode="server"
          paginationModel={paginationModel}
          columnGroupingModel={columnGroupingModel}
          onPaginationModelChange={handlePaginationModelChange}
          rowCount={searchTermsGrid?.total_rows || 0}
          pageSizeOptions={[10, 20, 50, 100]}
          sx={{
            '& .MuiDataGrid-toolbarContainer': {
              padding: 0,
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid rgba(145, 158, 171, 0.12)',
              minHeight: 53,
            },
          }}
          slots={{
            panel(props) {
              const apiRefContext = useGridApiContext();
              const [isPlaced, setIsPlaced] = useState(false);

              const handleClickAway = useCallback(() => {
                apiRefContext.current.hidePreferences();
              }, [apiRefContext]);

              const handleKeyDown = useCallback(
                (event: React.KeyboardEvent) => {
                  if (event.key === 'Escape') {
                    apiRefContext.current.hidePreferences();
                  }
                },
                [apiRefContext]
              );

              const modifiers = useMemo(
                () =>
                  [
                    {
                      name: 'flip',
                      enabled: true,
                      options: {
                        rootBoundary: 'document',
                      },
                    },
                    {
                      name: 'isPlaced',
                      enabled: true,
                      phase: 'main' as const,
                      fn: () => {
                        setIsPlaced(true);
                      },
                      effect: () => () => {
                        setIsPlaced(false);
                      },
                    },
                  ] as any,
                []
              );

              const open = apiRef.current?.state?.preferencePanel?.open;
              const panelType = apiRef.current?.state?.preferencePanel?.openedPanelValue;
              const anchorEl = toolbarRef.current;

              if (!anchorEl) {
                return null;
              }

              const panelProps = {
                ...props,
                as: undefined,
                anchorEl: anchorEl,
                open: open,
              };

              return (
                <GridPanelRoot
                  {...panelProps}
                  placement="bottom-end"
                  className={props.className}
                  modifiers={modifiers}
                >
                  <ClickAwayListener
                    mouseEvent="onPointerUp"
                    touchEvent={false}
                    onClickAway={handleClickAway}
                  >
                    <GridPaperRoot elevation={8} onKeyDown={handleKeyDown}>
                      {isPlaced && props.children}
                    </GridPaperRoot>
                  </ClickAwayListener>
                </GridPanelRoot>
              );
            },
            toolbar: Toolbar,
            filterPanel: (props) => (
              <FilterPanelCustom
                {...props}
                getColumnForNewFilter={(args) => (args ? args.columns[0].field : undefined)}
              />
            ),
            loadingOverlay(props, deprecatedLegacyContext) {
              return (
                <Skeleton variant="rectangular" height={60 * (paginationModel.pageSize + 2)} />
              );
            },
          }}
          slotProps={{
            toolbar: {
              printOptions: {
                disableToolbarButton: true,
              },
              csvOptions: {
                disableToolbarButton: true,
              },
            },
            filterPanel: {
              logicOperators: [GridLogicOperator.And, GridLogicOperator.Or],
            },
            loadingOverlay: {
              style: {
                backgroundColor: 'unset',
              },
            },
          }}
        />
      </CardContent>
    </Card>
  );
}
